import React, { useEffect, useState } from "react";
import { Card, Space, Button, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { Task } from "../../../constants/interface/task";
import { useTaskCompliance } from "../../../pages/Merchant/UploadDocument/api";
import { useDebounce } from "../../../utils";
import {
  productStatus,
  testPlanStatus,
} from "../../Atoms/status/common-status";

const TargetReviewCompletionDate = styled.div`
  font-weight: bold;
  font-size: 14px;
  display: flex;
  flex-direction: column;
`;

const InfoIcon = styled(InfoCircleOutlined)`
  color: #005eff;
`;

interface TaskDetailCardProps {
  task: Task;
  reload: () => void;
  onClickView: () => void;
  onClickConformityView: (compliance: any) => void;
  onEditExpiryDate?: () => void;
  showTestPlan?: boolean;
  onClickUploadTestPlan?: (compliance: any) => void;
}
export const TaskReviewDetailCard = React.forwardRef(
  (props: TaskDetailCardProps, ref) => {
    const { task, showTestPlan = false, onClickUploadTestPlan } = props;
    const [param, setParam] = useState({ taskId: task.id });
    const { data: complianceData, isLoading, retry } = useTaskCompliance(
      useDebounce(param, 200)
    );
    const renderConformityStatus = () => {
      if (task?.status === "rejected") return productStatus("rejected");
      if (task?.status === "pending_e_signature")
        return productStatus(task?.status);
      if (task?.status === "completed") {
        if (
          complianceData?.conformityCertificateSelection === "merchant_uploaded"
        )
          return productStatus(task?.status);
        return productStatus(complianceData?.conformityCertificateSelection);
      }

      return productStatus(task?.status);
    };

    React.useImperativeHandle(ref, () => {
      return {
        reload: () => {
          retry();
        },
      };
    });

    return (
      <CusCard
        title={<CusCardTitle>Compliance Check</CusCardTitle>}
        extra={
          <TargetReviewCompletionDate>
            <div>
              <Tooltip title="“na” means it is pending supplier’s submission. Target Review Completion Date will be calculated based on submission date.">
                <Space>
                  Target Review Completion Date
                  <InfoIcon />
                </Space>
              </Tooltip>
            </div>
            <div>{task.estimatedCompletionDate || "N/A"}</div>
          </TargetReviewCompletionDate>
        }
      >
        <Space direction="vertical" size={44}>
          <Space direction="vertical" size={2}>
            <h2>Document Validation</h2>
            <Space size={8}>
              {productStatus(
                task?.status === "completed" ||
                  task?.status === "pending_e_signature"
                  ? "passed"
                  : task?.status
              )}
              <Button type="link" onClick={props.onClickView}>
                View
              </Button>
            </Space>
          </Space>

          {showTestPlan && (
            <Space direction="vertical" size={2}>
              <h2>Test Plan</h2>
              {!isLoading && (
                <Space>
                  {testPlanStatus(
                    task?.testPlanStatus === "pending"
                      ? "missing_test_plan"
                      : task?.testPlanStatus,
                    true
                  )}
                  <Button
                    type="link"
                    onClick={() => {
                      onClickUploadTestPlan &&
                        onClickUploadTestPlan(complianceData);
                    }}
                  >
                    {task?.testPlanStatus === "auto_assigned"
                      ? "View"
                      : "Upload"}
                  </Button>
                </Space>
              )}
            </Space>
          )}

          <Space direction="vertical" size={2}>
            <h2>Conformity Certificate</h2>
            {!isLoading && (
              <Space>
                {renderConformityStatus()}
                <Button
                  type="link"
                  onClick={() => {
                    props.onClickConformityView(complianceData);
                  }}
                >
                  View
                </Button>
              </Space>
            )}
          </Space>
        </Space>
        <h2 style={{ marginTop: 30 }}>Expiry Date</h2>
        <Space>
          <div>{task.expiryDate ? task.expiryDate : "N/A"}</div>
          {props.onEditExpiryDate && task?.status !== "rejected" && (
            <Button onClick={props.onEditExpiryDate} type="link">
              Edit
            </Button>
          )}
        </Space>
        <h2 style={{ marginTop: 30 }}>Promote Code</h2>
        <Space>
          <div>{task.promoteCode ? task.promoteCode : "N/A"}</div>
        </Space>
      </CusCard>
    );
  }
);

const CusCard = styled(Card)`
  width: 100%;
  padding: 6px 24px 0px 24px;
  border-radius: 15px;
  position: relative;

  .ant-card-body {
    padding: 20px 0px;
  }

  .ant-card-head {
    padding: 0px 0px;
  }
`;

const Info = styled.div`
  color: #777777;
`;

const CusCardTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.03em;
  text-align: left;
`;
